import { AddressForm } from '../../components/address-form';
import { Button, ButtonVariants } from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  useAddressForm,
  useGetAddressAlertMessage,
} from '../../hooks/address-form.hook';
import { testId } from '../../utils';
import { CheckoutAddressStepProps } from './checkout-address';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { z } from 'zod';

type AddCheckoutAddressProps = Omit<
  CheckoutAddressStepProps,
  'addAddressForm' | 'editAddressForm'
> & {
  onCancel: () => void;
  addressForm: ReturnType<typeof useAddressForm>;
  hideCancel?: boolean;
};

export const AddAddress: React.FC<AddCheckoutAddressProps> = ({
  'data-testid': dataTestId,
  onContinue,
  onCancel,
  noDefault,
  addressForm,
  checkoutLoading,
  hideCancel,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const getAddressAlertMessage = useGetAddressAlertMessage();

  const { form, regionQuery, countries, countriesLoading, AddressSchema } =
    addressForm;
  const addressServerError = form.formState.errors.root?.serverError;

  type AddressSchema = z.infer<typeof AddressSchema>;

  const queryClient = useQueryClient();

  const addAddressMutation = Cornerstone.useAddAddress({
    mutation: {
      onSuccess: (response) => {
        onContinue?.(response.data);
        form.reset();
        queryClient.invalidateQueries({
          queryKey: Cornerstone.getGetUserAddressesQueryKey(),
        });
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to add address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });

        const errorMessage = getAddressAlertMessage(error);

        if (!errorMessage) return;

        form.setError('root.serverError', {
          message: errorMessage,
        });
      },
    },
  });

  const { regions, loading: regionsLoading, label: regionLabel } = regionQuery;
  const { handleSubmit } = form;

  const onSubmit = React.useCallback(
    (data: AddressSchema) => {
      addAddressMutation.mutate({ data });
    },
    [addAddressMutation],
  );

  return (
    <div {...testId(dataTestId)} className="flex flex-col gap-7">
      <RequiredFieldIndicator />
      {addressServerError && (
        <Alert {...testId(dataTestId, 'alert')} variant="error">
          {addressServerError.message}
        </Alert>
      )}
      <AddressForm
        loading={
          addAddressMutation.isLoading || countriesLoading || regionsLoading
        }
        countries={countries}
        regions={regions}
        regionLabel={regionLabel}
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        noDefault={noDefault}
      >
        <div className="flex flex-col md:flex-row gap-4">
          {onContinue && (
            <Button
              {...testId(dataTestId, 'continue')}
              size="sm"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              loading={addAddressMutation.isLoading}
              disabled={addAddressMutation.isLoading || checkoutLoading}
            >
              {t('auth.continue')}
            </Button>
          )}
          {!hideCancel && (
            <Button
              {...testId(dataTestId, 'cancel')}
              variant={ButtonVariants.Secondary}
              size="sm"
              type="button"
              onClick={onCancel}
              disabled={addAddressMutation.isLoading}
            >
              {t('user.settings.account.cancel_button')}
            </Button>
          )}
        </div>
      </AddressForm>
    </div>
  );
};
